import { initializeApp, getApps, getApp } from 'firebase/app';
import {
    getDatabase,
    ref,
    set,
    update,
    get,
    onValue,
    query,
    orderByChild,
    orderByValue,
    equalTo,
    limitToLast,
    serverTimestamp,
    runTransaction,
    push
} from 'firebase/database';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { SKILL_TREE } from '../data/adventureSkills.ts';
import type {
    Stone, Baguette, StarterDough, StarterStrainId, ActiveBake,
    IngredientType, Rarity,
} from '../data/PickleBarrel';

export type {
    Stone, Baguette, StarterDough, StarterStrainId, ActiveBake,
    IngredientType, Rarity,
};
import {
    STARTER_STRAINS, resolveBake, rollStarterDoughGacha,
    CRAFT_RECIPE, mergeStones, STONE_META,
    type StoneType
} from '../data/PickleBarrel';

// Firebase configuration provided by the user
const firebaseConfig = {
    apiKey: "AIzaSyCacmezQy1SfBMH3GYLJOyRoWVy04VnfEs",
    authDomain: "pickle-pong-e6707.firebaseapp.com",
    projectId: "pickle-pong-e6707",
    storageBucket: "pickle-pong-e6707.firebasestorage.app",
    messagingSenderId: "626598442741",
    appId: "1:626598442741:web:c588e1388fcc2a2a14b7bc",
    measurementId: "G-74V45L87LV",
    databaseURL: "https://pickle-pong-e6707-default-rtdb.firebaseio.com"
};

// Initialize Firebase
let app;
if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
} else {
    app = getApp();
}

let db: any;
let auth: any;
let functions: any;
let analytics: any;

try {
    db = getDatabase(app);
    auth = getAuth(app);
    functions = getFunctions(app, 'us-central1');

    isSupported().then(yes => yes ? analytics = getAnalytics(app) : null);
    console.log('[Firebase] Services initialized');
} catch (error: any) {
    console.error('[Firebase] All initialization attempts failed', error);
}

export { db, auth, functions, analytics };

let authPromise: Promise<any> | null = null;
let authStateResolved = false;

export const ensureAuth = async () => {
    if (!auth) throw new Error("Firebase Auth not initialized");

    // Wait for Firebase to fully load the persisted session from IndexedDB.
    // On mobile this takes 200-500ms — we MUST wait before deciding to go anonymous.
    if (!authStateResolved) {
        await new Promise<void>((resolve) => {
            const unsubscribe = auth.onAuthStateChanged(() => {
                authStateResolved = true;
                unsubscribe();
                resolve();
            });
        });
    }

    // If we have a real (non-anonymous) persisted session, use it.
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
        return auth.currentUser;
    }

    // CRITICAL: Never create an anonymous session if a verified wallet key exists
    // in localStorage. On mobile, the wallet's custom token session may still be
    // loading from IndexedDB when this function is first called. Creating an
    // anonymous session here would overwrite the real session — breaking the
    // Firebase auth state and causing the "Cannot remove last auth method" error
    // in cloud functions (since the server now sees an anonymous user with only
    // Discord, not the wallet-linked account).
    const hasVerifiedWallet = Object.keys(localStorage).some(
        k => k.startsWith('verified_') && localStorage.getItem(k) === 'true'
    );
    if (hasVerifiedWallet) {
        // Return whatever current user we have (may be null/anonymous temporarily).
        // The wallet session will take over once the wallet SDK finishes hydrating.
        return auth.currentUser;
    }

    // No real session, no wallet key — safe to create anonymous session.
    if (!authPromise) {
        authPromise = signInAnonymously(auth).catch((error) => {
            console.error('Firebase Anonymous Auth failed:', error);
            authPromise = null; // allow retry
            throw error;
        });
    }
    return authPromise;
};

// ============================================================================
// TYPES
// ============================================================================

export interface UserProfile {
    userId: string;
    walletAddress: string;
    displayName: string;
    profileImageUrl: string | null;
    profileImageAssetId?: number | null;
    bio?: string;
    createdAt: number | object;
    lastActiveAt: number | object;
    stats: {
        totalGamesPlayed: number;
        totalSoloWins: number;
        totalSoloLosses: number;
        multiplayerWins: number;
        multiplayerLosses: number;
        totalPoints: number;
        level: number;
        currentStreak: number;
        bestStreak: number;
        perfectWins?: number;
        adventure?: {
            bestLevel: number;
            totalBricks: number;
            totalRuns: number;
        };
    };
    unlockedTracks: string[];
    adventureProgress?: {
        brineTokens: number;
        unlockedSkills: string[];
        maxLevelReached: number;
        bossKills?: number;
        barrelInventory?: Record<string, any>;
        version?: string;
    };
    antiExploitLogs?: Record<string, number[]>;
    settings?: {
        backgroundUrls: string[];
        selectedPaddleModifier?: string;
        equippedTitle?: string;
        equippedBanner?: string;
        isColorblindMode?: boolean;
    };
    discordId?: string | null;
    discordUsername?: string | null;
    creditsSpent?: number;    // running total of credits spent on brewery
    brineCredits?: number;    // cached computed balance (totalPoints - creditsSpent)
    inventory?: {
        stones?:       Record<string, any>;
        baguettes?:    Record<string, any>;
        starterDoughs?: Record<string, any>;
        ingredients?:  { pit_dust?: number; brine_drops?: number; active_culture?: number; salt_stone?: number };
        barrels?:      Record<string, any>;
    };
    brewery?: {
        ovens?: {
            primary?:   any;
            secondary?: any;
        };
        secondOvenUnlocked?:   boolean;
        secondOvenUnlockedAt?: number | null;
        level5GiftClaimed?:    boolean;
    };
}

export const ErrorCode = {
    NONE: 'NONE',
    AUTH_REQUIRED: 'AUTH_REQUIRED',
    DB_NOT_READY: 'DB_NOT_READY',
    INVALID_NAME_FORMAT: 'INVALID_NAME_FORMAT',
    NAME_TAKEN: 'NAME_TAKEN',
    RESERVED_NAME: 'RESERVED_NAME',
    NETWORK_ERROR: 'NETWORK_ERROR',
    SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
    SESSION_FULL: 'SESSION_FULL',
    SESSION_NOT_WAITING: 'SESSION_NOT_WAITING',
    UNKNOWN: 'UNKNOWN'
} as const;

export type ErrorCode = typeof ErrorCode[keyof typeof ErrorCode];

export interface OperationResult {
    success: boolean;
    error?: ErrorCode;
    message?: string;
}

export interface GameSession {
    sessionId: string;
    createdBy: string;
    createdAt: number;
    status: 'waiting' | 'countdown' | 'playing' | 'finished';
    mode: 'normal' | 'chaos' | 'random';
    /** First to this many points wins (3, 5, or 7) */
    winThreshold: 3 | 5 | 7;
    /** If true, this match affects ranked stats (multiplayerWins/Losses) */
    ranked: boolean;
    /** Links to a tournament if this is a tournament match */
    tournamentId?: string;
    tournamentRound?: number;
    tournamentMatch?: string;
    players: {
        player1: {
            userId: string;
            displayName: string;
            profileImageUrl?: string | null;
            profileImageAssetId?: number | null;
            joinedAt: number;
            ready: boolean;
        };
        player2: {
            userId: string | null;
            displayName: string | null;
            profileImageUrl?: string | null;
            profileImageAssetId?: number | null;
            joinedAt: number | null;
            ready: boolean;
        };
    };
    gameState: {
        player1Score: number;
        player2Score: number;
        xpAwarded?: number;
    };
    updatedAt: number;
}

export interface SupportedCollection {
    id:               string;
    name:             string;
    description?:     string;
    avatarUrl?:       string;           // Collection avatar/banner image URL
    creatorAddresses: string[];
    unitNames:        string[];
    asaIds?:          number[];
    status?:           'pending' | 'active' | 'rejected';
    submittedBy?:     string;         // Wallet address that submitted
    paymentTxId?:     string;         // On-chain proof of $20 USDC payment
    verifiedWallets?: string[];       // Wallets proven owned by submitter
    rejectionReason?: string;         // Admin note when rejecting
    updatedAt:        number;
    createdAt?:       number;
}

export interface SupportedAsset {
    id:               number;         // ASA ID
    name:             string;
    unitName:         string;
    creatorAddress:   string;
    status:           'pending' | 'active' | 'rejected';
    submittedBy:      string;         // Wallet address
    paymentTxId?:     string;
    updatedAt:        number;
    createdAt?:       number;
}

// ============================================================================
// UTILITIES (Service Layer)
// ============================================================================

export const TRACK_REWARDS: Record<number, string> = {
    2: 'Retro Classic',
    3: 'Neon Nights',
    4: 'Grid Arena',
    5: 'Sunset Court',
    6: 'Frost Arena',
    7: 'Matrix Code',
    8: 'Lava Core',
    9: 'Deep Space',
    10: 'Cyber Circuit',
    12: 'Glitch Mode',
    18: 'Arcade Cabinet',
    25: 'Bronze Arena',
    30: 'Hologram Court',
    40: 'Void Arena',
    50: 'Silver Arena',
    60: 'Quantum Grid',
    75: 'Gold Arena',
    90: 'Infinity Arena',
    100: 'Platinum Arena'
};

export const firebaseUtils = {
    /**
     * Initialize or update a user profile based on their wallet address.
     */
    async initUserProfile(walletAddress: string, displayName?: string): Promise<UserProfile> {
        if (!db) throw new Error('Database not initialized');
        await ensureAuth();

        const upperAddress = walletAddress.toUpperCase();
        // Resolve UID
        const currentUid = auth.currentUser?.uid;
        if (!currentUid) {
            console.warn('[Firebase] No UID available for mapping');
            return null as any;
        }

        // Fix #1: ONLY write the mapping if the current user is NOT anonymous.
        // This prevents anonymous "ghost" sessions from overwriting verified wallet mappings.
        const currentUser = auth.currentUser;
        if (currentUser && !currentUser.isAnonymous) {
            const mappingRef = ref(db, `uid_mapping/${upperAddress}`);
            try {
                await set(mappingRef, currentUser.uid);
                console.log(`[Firebase] UID mapping verified for ${walletAddress}`);
            } catch (e) {
                console.warn('[Firebase] Failed to update wallet mapping.', e);
            }
        }

        const userRef = ref(db, `users/${upperAddress}`);

        // Add a timeout to prevent hanging on "offline" errors
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Firebase timeout: Client might be offline')), 8000)
        );

        try {
            const userSnap = await Promise.race([get(userRef), timeoutPromise]) as any;

            if (userSnap.exists()) {
                const userData = userSnap.val();
                const stats = {
                    ...(userData.stats || {}),
                    totalPoints: userData.stats?.totalPoints || 0,
                    level: Math.floor(Math.pow((userData.stats?.totalPoints || 0) / 100, 2 / 3)) + 1
                };
                const newStatFields = ['demonWins', 'perfectWins'];
                newStatFields.forEach(f => {
                    if (stats[f] === undefined) stats[f] = 0;
                });

                // Build the settings object from persisted data, filling in ONLY missing fields
                const persistedSettings = userData.settings || {};
                const settings = {
                    backgroundUrls: persistedSettings.backgroundUrls ?? [],
                    equippedTitle: persistedSettings.equippedTitle ?? 'rookie',
                    equippedBanner: persistedSettings.equippedBanner ?? 'retro_grid',
                    // CRITICAL: Preserve user's colorblind setting. Only default to false if truly absent.
                    isColorblindMode: persistedSettings.isColorblindMode ?? false,
                    selectedPaddleModifier: persistedSettings.selectedPaddleModifier ?? 'none',
                };

                const adventureProgress = userData.adventureProgress || {
                    brineTokens: 0,
                    unlockedSkills: [],
                    maxLevelReached: 0
                };

                // Write only the fields that are actually missing to avoid overwriting user preferences
                const missingSettings: Record<string, any> = {};
                if (!userData.settings) {
                    // Entire settings node is missing - safe to write all defaults
                    missingSettings['settings'] = settings;
                } else {
                    // Settings node exists - only patch missing sub-fields
                    if (persistedSettings.isColorblindMode === undefined) missingSettings['settings/isColorblindMode'] = false;
                    if (persistedSettings.equippedTitle === undefined) missingSettings['settings/equippedTitle'] = 'rookie';
                    if (persistedSettings.equippedBanner === undefined) missingSettings['settings/equippedBanner'] = 'retro_grid';
                    if (!Array.isArray(persistedSettings.backgroundUrls)) missingSettings['settings/backgroundUrls'] = [];
                    if (!userData.adventureProgress) {
                        missingSettings['adventureProgress'] = { brineTokens: 0, unlockedSkills: [], maxLevelReached: 0 };
                    }
                }

                const hasMigrations = Object.keys(missingSettings).length > 0;
                if (hasMigrations) {
                    try {
                        await update(userRef, { ...missingSettings, lastActiveAt: serverTimestamp(), walletAddress });
                    } catch (e) {
                        // Settings migration might fail if profile mapping is read-only
                    }
                }

                const finalProfile = { ...userData, stats, settings, adventureProgress, userId: walletAddress, walletAddress } as UserProfile;
                return finalProfile;
            } else {
                const newUser: any = {
                    walletAddress,
                    displayName: displayName || `Pickler_${walletAddress.slice(0, 6)}`,
                    profileImageUrl: null,
                    createdAt: serverTimestamp(),
                    lastActiveAt: serverTimestamp(),
                    stats: {
                        totalGamesPlayed: 0,
                        totalSoloWins: 0,
                        totalSoloLosses: 0,
                        multiplayerWins: 0,
                        multiplayerLosses: 0,
                        totalPoints: 0,
                        level: 1,
                        currentStreak: 0,
                        demonWins: 0,
                        perfectWins: 0,
                    },
                    adventureProgress: {
                        brineTokens: 0,
                        unlockedSkills: [],
                        maxLevelReached: 0
                    },
                    unlockedTracks: [],
                    antiExploitLogs: {},
                    settings: {
                        backgroundUrls: [],
                        equippedTitle: 'rookie',
                        equippedBanner: 'retro_grid',
                        isColorblindMode: false, // Default OFF
                    }
                };

                try {
                    await set(userRef, newUser);
                } catch (e) {
                    console.error('[Firebase] Failed to create new user profile:', e);
                    // Return the object anyway so the UI can render
                }

                return { ...newUser, userId: walletAddress } as UserProfile;
            }
        } catch (error) {
            console.error('[Firebase] initUserProfile failed (returning fallback):', error);
            // Return a minimal fallback profile so the UI doesn't crash/hide sections
            return {
                userId: walletAddress,
                walletAddress,
                displayName: `User_${walletAddress.slice(0, 6)}`,
                profileImageUrl: null,
                createdAt: Date.now(),
                lastActiveAt: Date.now(),
                stats: {
                    totalGamesPlayed: 0, totalSoloWins: 0, totalSoloLosses: 0,
                    multiplayerWins: 0, multiplayerLosses: 0, totalPoints: 0,
                    level: 1, currentStreak: 0, bestStreak: 0, demonWins: 0, perfectWins: 0
                },
                adventureProgress: {
                    brineTokens: 0,
                    unlockedSkills: [],
                    maxLevelReached: 1
                },
                unlockedTracks: [],
                settings: {
                    backgroundUrls: [],
                    equippedTitle: 'rookie',
                    equippedBanner: 'retro_grid',
                    isColorblindMode: false
                }
            } as UserProfile;
        }
    },

    /**
     * Create a new multiplayer game session.
     */
    async createGameSession(
        userId: string,
        displayName: string,
        mode: 'normal' | 'chaos' | 'random' = 'normal',
        profileImageUrl?: string | null,
        profileImageAssetId?: number | null,
        winThreshold: 3 | 5 | 7 = 7,
        ranked: boolean = true
    ): Promise<string> {
        const sessionsRef = ref(db, 'activeSessions');
        const newSessionRef = push(sessionsRef);

        const sessionData = {
            sessionId: newSessionRef.key,
            createdBy: userId,
            createdAt: serverTimestamp(),
            status: 'waiting',
            mode,
            winThreshold,
            ranked,
            players: {
                player1: {
                    userId,
                    displayName,
                    profileImageUrl: profileImageUrl || null,
                    profileImageAssetId: profileImageAssetId || null,
                    joinedAt: serverTimestamp(),
                    ready: false,
                },
                player2: {
                    userId: null,
                    displayName: null,
                    profileImageUrl: null,
                    profileImageAssetId: null,
                    joinedAt: null,
                    ready: false,
                },
            },
            gameState: {
                player1Score: 0,
                player2Score: 0,
            },
            updatedAt: serverTimestamp(),
        };
        await set(newSessionRef, sessionData);
        return newSessionRef.key as string;
    },

    /**
     * Subscribe to available "waiting" games for the lobby.
     */
    subscribeToLobby(callback: (games: GameSession[]) => void) {
        const waitingRef = query(ref(db, 'activeSessions'), orderByChild('status'), equalTo('waiting'));

        const unsubscribe = onValue(waitingRef, (snapshot) => {
            if (snapshot.exists()) {
                const games: GameSession[] = [];
                const now = Date.now();
                snapshot.forEach((childSnapshot) => {
                    const game = childSnapshot.val() as GameSession;
                    // Filter out games that are older than 10 minutes (600,000 ms)
                    if (now - game.createdAt <= 600000) {
                        games.push(game);
                    }
                });
                // Sort by createdAt descending (most recent first)
                games.sort((a, b) => b.createdAt - a.createdAt);
                callback(games);
            } else {
                callback([]);
            }
        });

        // onValue returns an unsubscribe function that we need to return
        return unsubscribe;
    },

    /**
     * Join an existing game session as Player 2.
     */
    async joinGameSession(
        sessionId: string,
        userId: string,
        displayName: string,
        profileImageUrl?: string | null,
        profileImageAssetId?: number | null
    ): Promise<OperationResult> {
        if (!db) return { success: false, error: ErrorCode.DB_NOT_READY };
        const sessionRef = ref(db, `activeSessions/${sessionId}`);

        try {
            const result = await runTransaction(sessionRef, (session: GameSession | null) => {
                if (!session) return null; // Transaction will return committed: true, snapshot: empty
                if (session.status !== 'waiting') return; // Abort
                if (session.players.player2?.userId && session.players.player1.userId !== userId && session.players.player2.userId !== userId) return; // Full

                // Handle re-entry (no changes needed)
                if (session.players.player1.userId === userId || session.players.player2?.userId === userId) {
                    return session;
                }

                // New player joining
                session.players.player2.userId = userId;
                session.players.player2.displayName = displayName;
                session.players.player2.profileImageUrl = profileImageUrl || null;
                session.players.player2.profileImageAssetId = profileImageAssetId || null;
                session.players.player2.joinedAt = serverTimestamp() as any;
                session.players.player2.ready = false;
                session.updatedAt = serverTimestamp() as any;
                return session;
            });

            if (!result.committed) {
                // Determine why it wasn't committed (status or full)
                const snap = await get(sessionRef);
                if (snap.exists()) {
                    const session = snap.val();
                    if (session.status !== 'waiting') return { success: false, error: ErrorCode.SESSION_NOT_WAITING, message: 'Game has already started.' };
                    if (session.players.player2?.userId) return { success: false, error: ErrorCode.SESSION_FULL, message: 'Game is already full.' };
                }
                return { success: false, error: ErrorCode.UNKNOWN };
            }

            if (result.snapshot.val() === null) {
                return { success: false, error: ErrorCode.SESSION_NOT_FOUND, message: 'Game not found.' };
            }

            return { success: true, error: ErrorCode.NONE };
        } catch (error) {
            console.error('[Firebase] joinGameSession failed:', error);
            return { success: false, error: ErrorCode.NETWORK_ERROR };
        }
    },

    /**
     * Cancel an active game session from the lobby. Only game creators should call this for their own games.
     */
    async cancelGameSession(sessionId: string) {
        if (!db) throw new Error('Database not initialized');
        const sessionRef = ref(db, `activeSessions/${sessionId}`);
        await set(sessionRef, null);
    },

    /**
     * Transitions a countdown game to the active playing state.
     */
    async startGameSession(sessionId: string) {
        if (!db) throw new Error('Database not initialized');
        const sessionRef = ref(db, `activeSessions/${sessionId}`);
        await update(sessionRef, {
            status: 'playing',
            startedAt: Date.now(),
            updatedAt: serverTimestamp()
        });
    },

    /**
     * Real-time subscription to a specific game session.
     */
    subscribeToGame(sessionId: string, callback: (session: GameSession | null) => void) {
        const sessionRef = ref(db, `activeSessions/${sessionId}`);

        const unsubscribe = onValue(sessionRef, (snapshot) => {
            callback(snapshot.exists() ? (snapshot.val() as GameSession) : null);
        });

        return unsubscribe;
    },

    /**
     * Update real-time scores during a match.
     */
    async toggleReady(sessionId: string, userId: string, ready: boolean) {
        const sessionRef = ref(db, `activeSessions/${sessionId}`);

        await runTransaction(sessionRef, (session: GameSession | null) => {
            if (!session || session.status !== 'waiting') {
                return session;
            }

            // Determine which player is toggling
            if (session.players.player1.userId === userId) {
                session.players.player1.ready = ready;
            } else if (session.players.player2.userId === userId) {
                session.players.player2.ready = ready;
            }

            // If both players are present and ready, advance state
            if (session.players.player1.ready && session.players.player2?.ready) {
                session.status = 'countdown';
                session.updatedAt = Date.now();
            }

            return session;
        });
    },

    async updateScore(sessionId: string, p1Score: number, p2Score: number) {
        await update(ref(db, `activeSessions/${sessionId}/gameState`), {
            player1Score: p1Score,
            player2Score: p2Score,
        });
        await update(ref(db, `activeSessions/${sessionId}`), {
            updatedAt: serverTimestamp()
        });
    },

    /**
     * Track a newly minted custom avatar asset ID in Firebase to avoid expensive blockchain scans.
     */
    async addMintedAvatar(userId: string, assetId: number): Promise<void> {
        if (!db) return;
        const upperId = userId.toUpperCase();
        const avatarRef = ref(db, `users/${upperId}/mintedAvatars/${assetId}`);
        await set(avatarRef, {
            assetId,
            timestamp: serverTimestamp()
        });
        console.log(`[Firebase] Tracked minted avatar ${assetId} for ${userId}`);
    },

    /**
     * Get the list of asset IDs minted by a user.
     */
    async getMintedAvatars(userId: string): Promise<number[]> {
        if (!db) return [];
        const upperId = userId.toUpperCase();
        const snap = await get(ref(db, `users/${upperId}/mintedAvatars`));
        if (snap.exists()) {
            return Object.keys(snap.val()).map(id => Number(id));
        }
        return [];
    },

    /**
     * Remove a tracked avatar (e.g. after destruction).
     */
    async removeMintedAvatar(userId: string, assetId: number): Promise<void> {
        if (!db) return;
        const upperId = userId.toUpperCase();
        await set(ref(db, `users/${upperId}/mintedAvatars/${assetId}`), null);
    },

    /**
     * Track a newly minted custom arena asset ID in Firebase.
     */
    async addMintedArena(userId: string, assetId: number): Promise<void> {
        if (!db) return;
        const upperId = userId.toUpperCase();
        const arenaRef = ref(db, `users/${upperId}/mintedArenas/${assetId}`);
        await set(arenaRef, {
            assetId,
            timestamp: serverTimestamp()
        });
        console.log(`[Firebase] Tracked minted arena ${assetId} for ${userId}`);
    },

    /**
     * Get the list of arena asset IDs minted by a user.
    async getMintedArenas(userId: string): Promise<number[]> {
        if (!db) return [];
        const upperId = userId.toUpperCase();
        const snap = await get(ref(db, `users/${upperId}/mintedArenas`));
        if (snap.exists()) {
            return Object.keys(snap.val()).map(id => Number(id));
        }
        return [];
    },

    /**
     * Globally track a newly minted Arena asset ID in Firebase.
     */
    async registerArenaAsset(assetId: number, creatorWallet: string): Promise<void> {
        if (!db) return;
        const r = ref(db, `arena_assets/${assetId}`);
        await set(r, {
            assetId,
            creatorWallet: creatorWallet.toUpperCase(),
            mintedAt: Date.now(),
        });
        console.log(`[Firebase] Registered global Arena asset ${assetId}`);
    },

    /**
     * Get all known Arena asset IDs from the global registry.
     */
    async getArenaAssetIds(): Promise<number[]> {
        if (!db) return [];
        const snap = await get(ref(db, 'arena_assets'));
        if (!snap.exists()) return [];
        return Object.keys(snap.val()).map(Number);
    },

    async registerArenaListing(listing: { 
        appId: number; 
        assetId: number; 
        seller: string; 
        price: number;
        amount: number;
        listedAt: number;
        // Display fields — store once at list time, read back freely from Firebase
        name?: string;
        imageUrl?: string;
        unitName?: string;
        totalSupply?: number;
    }) {
        if (!db) return;
        const r = ref(db, `arena_listings/${listing.appId}`);
        await set(r, {
            ...listing,
            updatedAt: serverTimestamp(),
        });
        console.log(`[Firebase] Registered Arena listing for appId ${listing.appId}`);
    },

    /**
     * Remove a listing from the registry (on buy or delist).
     */
    async removeArenaListing(appId: number) {
        if (!db) return;
        await set(ref(db, `arena_listings/${appId}`), null);
        console.log(`[Firebase] Removed Arena listing for appId ${appId}`);
    },

    /**
     * Mark a listing as pending sold to hide it from the UI immediately.
     * Used as a fallback if the final delete write fails after an on-chain buy.
     */
    async markListingAsPendingSold(appId: number): Promise<void> {
        if (!db) return;
        const r = ref(db, `arena_listings/${appId}`);
        await update(r, {
            pendingSold: true,
            pendingSoldAt: serverTimestamp(),
        });
        console.log(`[Firebase] Marked Arena listing as pendingSold for appId ${appId}`);
    },

    /**
     * Get all active Arena listings from the registry.
     */
    async getArenaListings(): Promise<any[]> {
        if (!db) return [];
        const snap = await get(ref(db, 'arena_listings'));
        if (!snap.exists()) return [];
        return Object.values(snap.val());
    },

    async getSupportedAssets(): Promise<SupportedAsset[]> {
        if (!db) return [];
        const snap = await get(ref(db, 'supported_assets'));
        if (!snap.exists()) return [];
        return Object.values(snap.val());
    },

    /**
     * Patch only the imageUrl field on an existing Arena listing.
     * Called by the client after it self-resolves a raw ipfs:// URI so that
     * future loads skip the gateway probe entirely (zero-probe reads).
     */
    async patchArenaListingImageUrl(appId: number, imageUrl: string): Promise<void> {
        if (!db || !imageUrl) return;
        await update(ref(db, `arena_listings/${appId}`), { imageUrl });
    },

    async finishGame(sessionId: string) {
        if (!functions) throw new Error('Functions not initialized');
        const secureFinish = httpsCallable(functions, 'finishMultiplayerGame');
        try {
            const result = await secureFinish({ sessionId }) as any;
            return result.data.success ? result.data.matchId : null;
        } catch (error) {
            console.error('Secure finishMultiplayerGame failed:', error);
            throw error;
        }
    },

    /**
     * Finish a multiplayer game with ranked/casual distinction.
     * Ranked games update multiplayerWins/Losses and award more XP.
     * Casual games award flat XP with no stat impact.
     */
    async finishMultiplayerGameRanked(
        sessionId: string,
        ranked: boolean = true
    ): Promise<{ matchId: string | null; xpAwarded: number }> {
        if (!functions) throw new Error('Functions not initialized');
        const secureFinish = httpsCallable(functions, 'finishMultiplayerGame');
        try {
            const result = await secureFinish({ sessionId, ranked }) as any;
            const data = result.data;
            return {
                matchId: data.success ? data.matchId : null,
                xpAwarded: data.xpAwarded ?? 0,
            };
        } catch (error: any) {
            console.error('[Firebase] finishMultiplayerGameRanked failed:', {
                message: error.message,
                code: error.code,
                details: error.details,
                sessionId,
                ranked
            });
            // Non-fatal — return graceful fallback so the UI still shows the result screen
            return { matchId: null, xpAwarded: 0 };
        }
    },

    /**
     * Write this client's local paddle Y to the Firebase live sync path.
     * Throttled externally — call this from a 50ms interval, not every frame.
     */
    writePaddlePos(sessionId: string, isPlayer1: boolean, y: number): void {
        if (!db) return;
        const path = isPlayer1 ? 'p1Y' : 'p2Y';
        // Isolated 'liveSync' path prevents main GameSession re-renders
        set(ref(db, `liveSync/${sessionId}/${path}`), y).catch(() => {
            // Silently absorb — a missed paddle frame is not fatal
        });
    },

    /**
     * Subscribe to the OPPONENT's paddle Y position.
     * isPlayer1 = true → listens for p2Y (opponent is player 2).
     * isPlayer1 = false → listens for p1Y (opponent is player 1).
     * Returns an unsubscribe function.
     */
    subscribeToPaddlePos(
        sessionId: string,
        isPlayer1: boolean,
        callback: (y: number) => void
    ): () => void {
        if (!db) return () => {};
        // We listen to the OPPONENT's path in the isolated liveSync node
        const path = isPlayer1 ? 'p2Y' : 'p1Y';
        const paddleRef = ref(db, `liveSync/${sessionId}/${path}`);
        const unsubscribe = onValue(paddleRef, (snap) => {
            if (snap.exists()) callback(snap.val() as number);
        });
        return unsubscribe;
    },

    /**
     * Terminate a Solo game and award points based on difficulty.
     * Solo games only award points on a WIN.
     */
    async finishSoloGame(isWinner: boolean, difficulty: string, isPerfect: boolean, mode: string = 'normal') {
        if (!functions) throw new Error('Functions not initialized');

        // Fix #2: Auth Guard — never call Cloud Functions without an auth session
        const currentUser = auth.currentUser;
        if (!currentUser) {
            console.warn('[Firebase] finishSoloGame skipped — no active auth session');
            return null;
        }

        const secureFinish = httpsCallable(functions, 'finishSoloGame');
        try {
            // Fix #3: Removed userId from payload since Cloud Function exclusively uses context.auth.uid
            const result = await secureFinish({ isWinner, difficulty, isPerfect, mode }) as any;
            return result.data.success ? { matchId: result.data.matchId, pointsAwarded: result.data.pointsAwarded } : null;
        } catch (error) {
            console.error('Secure finishSoloGame failed:', error);
            throw error;
        }
    },

    /**
     * Terminate a Training game (Pickle Lab).
     * Grants 1 XP (Easy Mode equivalent) which counts towards the global leaderboard.
     * Does NOT increment stats like totalGamesPlayed, streaks, or win trackers.
     */
    async finishTrainingMatch(isWinner: boolean) {
        return this.finishSoloGame(isWinner, 'easy', false, 'training');
    },

    /**
     * Update a user's profile information, enforcing globally unique usernames.
     */
    async updateUserProfile(userId: string, data: Partial<Pick<UserProfile, 'displayName' | 'profileImageUrl' | 'profileImageAssetId' | 'bio'>>): Promise<OperationResult> {
        if (!db) return { success: false, error: ErrorCode.DB_NOT_READY };
        const upperId = userId.toUpperCase();

        // Handle Simple Avatar Update
        if (!data.displayName) {
            const updates = {
                ...data,
                lastActiveAt: serverTimestamp(),
            };
            await update(ref(db, `users/${upperId}`), updates);
            
            // Sync to active tournaments
            try {
                const { tournamentUtils } = await import('./tournamentUtils');
                await tournamentUtils.syncUserIdentityToTournaments(upperId, data);
            } catch (e) {
                console.warn('[Firebase] Tournament identity sync failed (avatar only):', e);
            }

            // Sync full profile to leaderboard to ensure points/stats are included
            const freshSnap = await get(ref(db, `users/${upperId}`));
            if (freshSnap.exists()) {
                await this.syncLeaderboardStats(upperId, freshSnap.val());
            }

            return { success: true, error: ErrorCode.NONE };
        }

        // Handle DisplayName Update (Regex Validation + Uniqueness)
        const proposedName = data.displayName.trim();
        const usernameRegex = /^[a-zA-Z0-9_]{3,15}$/;

        if (!usernameRegex.test(proposedName)) {
            return { success: false, error: ErrorCode.INVALID_NAME_FORMAT, message: 'Name must be 3-15 characters (letters, numbers, underscores only)' };
        }

        const lowercaseName = proposedName.toLowerCase();
        const reservedKeywords = ['fart', 'admin', 'mod', 'system']; // Example reserved list just in case
        if (reservedKeywords.includes(lowercaseName)) {
            return { success: false, error: ErrorCode.RESERVED_NAME, message: 'This name cannot be used.' };
        }

        const usernameRef = ref(db, `usernames/${lowercaseName}`);

        // Username is officially claimed.
        // 1. Get current user document
        const userRef = ref(db, `users/${userId}`);
        const userSnap = await get(userRef);
        let oldName = '';

        if (userSnap.exists()) {
            const oldUser = userSnap.val() as UserProfile;
            oldName = oldUser.displayName;

            // If name hasn't changed, skip uniqueness check and transaction
            if (oldName && oldName.toLowerCase() === lowercaseName) {
                const updates = {
                    ...data,
                    displayName: proposedName,
                    lastActiveAt: serverTimestamp(),
                };

                await update(ref(db, `users/${upperId}`), updates);

                // Sync to active tournaments
                try {
                    const { tournamentUtils } = await import('./tournamentUtils');
                    await tournamentUtils.syncUserIdentityToTournaments(upperId, {
                        displayName: proposedName,
                        profileImageUrl: data.profileImageUrl
                    });
                } catch (e) {
                    console.warn('[Firebase] Tournament identity sync failed (name only):', e);
                }

                // Sync to leaderboard
                await this.syncLeaderboardStats(upperId, {
                    ...data,
                    displayName: proposedName
                });
                return { success: true, error: ErrorCode.NONE };
            }
        }

        // Attempt to claim the NEW username
        const transactionResult = await runTransaction(usernameRef, (currentData) => {
            if (currentData === null) {
                // Name is free. Claim it!
                return userId;
            } else {
                // Name is taken
                return; // Abort transaction
            }
        });

        if (!transactionResult.committed) {
            return { success: false, error: ErrorCode.NAME_TAKEN, message: 'That name is already taken.' };
        }

        // Release the old name if it wasn't a default one
        if (oldName && oldName.toLowerCase() !== lowercaseName) {
            if (!oldName.toLowerCase().startsWith('pickler_')) {
                const oldUsernameRef = ref(db, `usernames/${oldName.toLowerCase()}`);
                await set(oldUsernameRef, null);
            }
        }

        // 2. Finally Update the main User Document
        const finalUpdates = {
            ...data,
            displayName: proposedName,
            lastActiveAt: serverTimestamp(),
        };
        await update(ref(db, `users/${upperId}`), finalUpdates);

        // Sync to active tournaments
        try {
            const { tournamentUtils } = await import('./tournamentUtils');
            await tournamentUtils.syncUserIdentityToTournaments(upperId, {
                displayName: proposedName,
                profileImageUrl: data.profileImageUrl
            });
        } catch (e) {
            console.warn('[Firebase] Tournament identity sync failed (full update):', e);
        }

        // 3. Sync to leaderboard (fetch full profile first to be safe)
        const updatedSnap = await get(ref(db, `users/${upperId}`));
        if (updatedSnap.exists()) {
            await firebaseUtils.syncLeaderboardStats(upperId, updatedSnap.val());
        }

        return { success: true, error: ErrorCode.NONE };
    },

    /**
     * Update user's selected NFT background URLs (array for random multi-pick).
     */
    async updateUserBackground(userId: string, backgroundUrls: string[]): Promise<{ success: boolean; error?: string }> {
        if (!db) return { success: false, error: 'Database not initialized' };
        const upperId = userId.toUpperCase();

        try {
            const userRef = ref(db, `users/${upperId}/settings`);
            await update(userRef, { backgroundUrls });
            return { success: true };
        } catch (error: any) {
            console.error('Failed to update background URLs:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Update user's selected paddle modifier.
     */
    async updateUserPaddleModifier(userId: string, selectedPaddleModifier: string): Promise<{ success: boolean; error?: string }> {
        if (!db) return { success: false, error: 'Database not initialized' };
        const upperId = userId.toUpperCase();

        try {
            const userRef = ref(db, `users/${upperId}/settings`);
            await update(userRef, { selectedPaddleModifier });
            return { success: true };
        } catch (error: any) {
            console.error('Failed to update paddle modifier:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Update user's selected Title and Banner for the Match Intro.
     */
    async updateUserIdentitySettings(userId: string, equippedTitle: string, equippedBanner: string): Promise<{ success: boolean; error?: string }> {
        if (!db) return { success: false, error: 'Database not initialized' };
        const upperId = userId.toUpperCase();

        try {
            const userRef = ref(db, `users/${upperId}/settings`);
            await update(userRef, { equippedTitle, equippedBanner });

            return { success: true };
        } catch (error: any) {
            console.error('Failed to update identity settings:', error);
            return { success: false, error: error.message };
        }
    },


    /**
     * Update user's selected Baguette/Paddle modifier.
     */
    async updateUserBaguette(userId: string, modifierId: string): Promise<{ success: boolean; error?: string }> {
        if (!db) return { success: false, error: 'Database not initialized' };
        const upperId = userId.toUpperCase();

        try {
            const userRef = ref(db, `users/${upperId}/settings`);
            await update(userRef, { selectedPaddleModifier: modifierId });
            return { success: true };
        } catch (error: any) {
            console.error('Failed to update baguette selection:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Generic settings update.
     */
    async updateUserSettings(userId: string, settings: Record<string, any>): Promise<{ success: boolean; error?: string }> {
        if (!db) return { success: false, error: 'Database not initialized' };
        const upperId = userId.toUpperCase();

        try {
            const userRef = ref(db, `users/${upperId}/settings`);
            await update(userRef, settings);
            return { success: true };
        } catch (error: any) {
            console.error('Failed to update settings:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Update user's colorblind mode setting.
     */
    async updateColorblindMode(userId: string, isColorblindMode: boolean): Promise<{ success: boolean; error?: string }> {
        if (!db) return { success: false, error: 'Database not initialized' };
        const upperId = userId.toUpperCase();

        try {
            const userRef = ref(db, `users/${upperId}/settings`);
            await update(userRef, { isColorblindMode });
            return { success: true };
        } catch (error: any) {
            console.error('Failed to update colorblind mode:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * SECURITY: Force-unlink a wallet from its current Firebase UID mapping.
     * This is used to resolve SECURITY_CONFLICT errors when a user migrates devices/sessions.
     */
    async unlinkWallet(walletAddress: string): Promise<OperationResult> {
        if (!functions) throw new Error("Functions not initialized");
        const secureUnlink = httpsCallable(functions, 'unlinkWallet');
        const upperWalletAddress = walletAddress.toUpperCase();

        try {
            await secureUnlink({ walletAddress: upperWalletAddress });
            console.log(`[Firebase] Wallet ${upperWalletAddress} unlinked via secure Cloud Function.`);
            return { success: true };
        } catch (error: any) {
            console.error('[Firebase] unlinkWallet failed:', error);
            return { success: false, error: ErrorCode.NETWORK_ERROR, message: error.message };
        }
    },

    /**
     * Fetch top players for the leaderboard from the flat leaderboard_stats node.
     */
    async getLeaderboard(limitCount = 50, sortBy: 'xp' | 'wins' = 'xp'): Promise<UserProfile[]> {
        const orderKey = sortBy === 'wins' ? 'multiplayerWins' : 'totalPoints';
        const usersRef = query(ref(db, 'leaderboard_stats'), orderByChild(orderKey), limitToLast(limitCount));
        const snapshot = await get(usersRef);

        if (!snapshot.exists()) return [];

        const users: UserProfile[] = [];
        snapshot.forEach((child) => {
            const data = child.val();
            users.push({
                userId: child.key,
                walletAddress: child.key,
                displayName: data.displayName,
                profileImageUrl: data.profileImageUrl || null,
                profileImageAssetId: data.profileImageAssetId || null,
                bio: data.bio || '',
                createdAt: data.lastUpdate || 0,
                lastActiveAt: data.lastUpdate || 0,
                stats: {
                    totalPoints: data.totalPoints || 0,
                    multiplayerWins: data.multiplayerWins || 0,
                    multiplayerLosses: data.multiplayerLosses || 0,
                    level: data.level || 1,
                    totalGamesPlayed: data.totalGamesPlayed || (data.multiplayerWins || 0) + (data.multiplayerLosses || 0),
                    totalSoloWins: data.totalSoloWins || 0,
                    totalSoloLosses: data.totalSoloLosses || 0,
                    demonWins: data.demonWins || 0,
                    perfectWins: data.perfectWins || 0,
                    currentStreak: 0,
                    bestStreak: 0
                },
                unlockedTracks: []
            } as UserProfile);
        });

        // Firebase order by is ascending, so we need to reverse it for top scores
        return users.reverse();
    },

    async syncLeaderboardStats(userId: string, data: any) {
        if (!db) return;
        const upperId = userId.toUpperCase();

        // Always fetch the full user profile so we can satisfy the
        // Firebase rule: leaderboard_stats/$wallet requires displayName
        let baseData = data;
        if (!data.displayName) {
            try {
                const snap = await get(ref(db, `users/${upperId}`));
                if (snap.exists()) baseData = { ...snap.val(), ...data };
            } catch (e) {
                console.warn('[Firebase] syncLeaderboardStats failed to fetch base profile:', e);
            }
        }

        // Use node-specific updates to avoid root-level permission blocks
        const updates: Record<string, any> = {};

        if (baseData.displayName) updates.displayName = baseData.displayName;
        else {
            console.warn(`[Firebase] syncLeaderboardStats skipped for ${upperId} — no displayName found to satisfy security rules`);
            return;
        }

        // Always sync profileImageUrl — even if null — to clear stale values
        if (baseData.profileImageUrl !== undefined) updates.profileImageUrl = baseData.profileImageUrl || null;
        if (baseData.profileImageAssetId !== undefined) updates.profileImageAssetId = baseData.profileImageAssetId || null;
        if (baseData.bio !== undefined) updates.bio = baseData.bio;

        // Sync Stats
        if (baseData.stats) {
            const s = baseData.stats;
            if (s.totalPoints !== undefined) updates.totalPoints = s.totalPoints;
            if (s.multiplayerWins !== undefined) updates.multiplayerWins = s.multiplayerWins;
            if (s.multiplayerLosses !== undefined) updates.multiplayerLosses = s.multiplayerLosses;
            if (s.level !== undefined) updates.level = s.level;
            if (s.totalSoloWins !== undefined) updates.totalSoloWins = s.totalSoloWins;
            if (s.totalSoloLosses !== undefined) updates.totalSoloLosses = s.totalSoloLosses;
            if (s.totalGamesPlayed !== undefined) updates.totalGamesPlayed = s.totalGamesPlayed;
            if (s.demonWins !== undefined) updates.demonWins = s.demonWins;
            if (s.perfectWins !== undefined) updates.perfectWins = s.perfectWins;
            if (s.currentStreak !== undefined) updates.currentStreak = s.currentStreak;
            if (s.maxStreak !== undefined) updates.maxStreak = s.maxStreak;

            if (s.adventure && s.adventure.bestLevel !== undefined) {
                updates.adventureBestLevel = s.adventure.bestLevel;
            }
        }

        // Also sync settings if available
        if (baseData.settings) {
            if (baseData.settings.equippedTitle) updates.equippedTitle = baseData.settings.equippedTitle;
            if (baseData.settings.equippedBanner) updates.equippedBanner = baseData.settings.equippedBanner;
        }

        if (Object.keys(updates).length > 0) {
            updates.lastUpdate = serverTimestamp();
            await update(ref(db, `leaderboard_stats/${upperId}`), updates);
        }
    },

    /**
     * ATOMIC PIPELINE: Award points -> Re-fetch profile -> Sync leaderboard.
     * Use this in Play.tsx to ensure reliable UI updates and persistence.
     */
    async finishSoloGameAndSync(
        isWinner: boolean,
        difficulty: string,
        isPerfect: boolean,
        mode: string = 'normal',
        walletAddress: string
    ): Promise<{ matchId: string | null; pointsAwarded: number; newProfile: any | null }> {

        // Step 1: Award points via Cloud Function
        const result = await this.finishSoloGame(isWinner, difficulty, isPerfect, mode);
        if (!result) return { matchId: null, pointsAwarded: 0, newProfile: null };

        // Step 2: Re-fetch the updated profile (source of truth)
        const upperId = walletAddress.toUpperCase();
        let newProfile: any | null = null;
        try {
            const snap = await get(ref(db, `users/${upperId}`));
            if (snap.exists()) {
                newProfile = { ...snap.val(), userId: walletAddress, walletAddress };
            }
        } catch (e) {
            console.warn('[Firebase] Failed to re-fetch profile after solo game:', e);
        }

        // Step 3: Sync the fresh data to leaderboard_stats
        if (newProfile) {
            try {
                await this.syncLeaderboardStats(upperId, newProfile);
            } catch (e) {
                console.warn('[Firebase] Leaderboard sync failed after solo game:', e);
            }
        }

        return { matchId: result.matchId, pointsAwarded: result.pointsAwarded, newProfile };
    },

    /**
     * Log a detailed match result to the game_history node (Tier 1).
     */
    async logGameResult(result: any) {
        if (!db) return null;
        const historyRef = ref(db, 'game_history');
        const newLogRef = push(historyRef);
        await set(newLogRef, {
            ...result,
            createdAt: serverTimestamp()
        });
        console.log('[Firebase] Game result logged to history');
        return newLogRef.key;
    },

    /**
     * Terminate an Adventure Mode run and update progress.
     * Only awards a Brine Token if this is the first time the level has been cleared.
     */
    async finishAdventureRun(walletAddress: string, run: {
        startLevel: number,
        endLevel: number,
        bricks: number,
        timeSeconds: number,
        win: boolean,
        isBossLevel?: boolean,
        prevBestLevel?: number
    }) {
        if (!functions) throw new Error('Functions not initialized');

        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.isAnonymous) {
            console.warn('[Firebase] finishAdventureRun skipped — no verified auth session');
            return null;
        }

        const secureFinish = httpsCallable(functions, 'finishAdventureRun');
        try {
            const result = await secureFinish(run) as any;
            if (result.data.success) {
                // Determine if this was a first-time clear
                const isFirstClear = run.endLevel > (run.prevBestLevel ?? 0);
                const isBossLevel = run.isBossLevel ?? (run.endLevel % 5 === 0);

                // For regular levels, if it's a first-time clear, sync tokens.
                // Boss levels are handled separately by recordBossKill.
                if (isFirstClear && !isBossLevel) {
                    await this.syncBrineTokens(walletAddress);
                }
                return result.data.matchId || 'SECURE_TRANS_SUCCESS';
            }
            return null;
        } catch (error) {
            console.error('Secure finishAdventureRun failed:', error);
            throw error;
        }
    },

    /**
     * Purchase an Adventure Mode skill node using Brine Tokens.
     */
    async purchaseSkill(_userId: string, skillId: string, cost: number): Promise<OperationResult> {
        if (!functions) return { success: false, error: ErrorCode.DB_NOT_READY };

        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.isAnonymous) {
            console.warn('[Firebase] purchaseSkill skipped — no verified auth session');
            return { success: false, message: 'Auth required' };
        }

        const securePurchase = httpsCallable(functions, 'purchaseSkill');

        try {
            const result = await securePurchase({ skillId, cost }) as any;
            if (result.data.success) {
                return { success: true };
            } else {
                return { success: false, message: 'Purchase failed server-side' };
            }
        } catch (error: any) {
            console.error('Error purchasing skill:', error);
            return { success: false, message: error.message };
        }
    },

    /**
     * One-time utility to sync existing users to the new leaderboard node.
     */
    async backfillLeaderboard() {
        const usersSnap = await get(ref(db, 'users'));
        if (usersSnap.exists()) {
            const users = usersSnap.val();
            for (const uid in users) {
                await this.syncLeaderboardStats(uid, users[uid]);
            }
            console.log('[Firebase] Leaderboard backfill complete');
        }
    },

    /**
     * Calculate required XP for a specific level.
     * Formula: 1000 * 2^(L-2) for L >= 2. (Level 1 requires 0 to reach).
     */
    getXpRequirementForLevel(level: number): number {
        if (level <= 1) return 0;
        // Smoother curve: 100, 282, 519, 800...
        return Math.floor(100 * Math.pow(level - 1, 1.5));
    },

    /**
     * Process level ups and track unlocks for a user data object during a transaction.
     * Mutates the userData object in-place.
     */
    processLevelUp(userData: any): { newLevelsGained: number, newlyUnlockedTracks: string[] } {
        let newLevelsGained = 0;
        const newlyUnlockedTracks: string[] = [];

        if (!userData.unlockedTracks) userData.unlockedTracks = [];

        while (true) {
            const nextLevel = userData.stats.level + 1;
            const requiredXp = this.getXpRequirementForLevel(nextLevel);

            if (userData.stats.totalPoints >= requiredXp) {
                // Level Up!
                userData.stats.level = nextLevel;
                newLevelsGained++;

                // Check for track unlock
                const rewardTrack = TRACK_REWARDS[nextLevel];
                if (rewardTrack && !userData.unlockedTracks.includes(rewardTrack)) {
                    userData.unlockedTracks.push(rewardTrack);
                    newlyUnlockedTracks.push(rewardTrack);
                }
            } else {
                break; // Not enough XP for next level
            }
        }

        return { newLevelsGained, newlyUnlockedTracks };
    },

    /**
     * Fetch a match result. Checks game_history and adventure_history.
     */
    async getMatchData(matchId: string): Promise<any> {
        if (!db) return null;
        let snap = await get(ref(db, `game_history/${matchId}`));
        if (snap.exists()) return snap.val();

        snap = await get(ref(db, `adventure_history/${matchId}`));
        if (snap.exists()) {
            const data = snap.val();
            data.mode = 'adventure';
            return data;
        }
        return null;
    },

    /**
     * Fetch a public profile by username
     */
    async getPublicProfileByUsername(username: string): Promise<UserProfile | null> {
        if (!db) return null;
        try {
            const usernameSnap = await get(ref(db, `usernames/${username.toLowerCase()}`));
            if (!usernameSnap.exists()) return null;
            const uid = usernameSnap.val();

            // Find primary wallet for this UID (first one in mapping)
            const mappingSnap = await get(query(ref(db, 'uid_mapping'), orderByValue(), equalTo(uid)));
            if (!mappingSnap.exists()) return null;

            const wallets = Object.keys(mappingSnap.val());
            const primaryWallet = wallets[0];
            const profile = await this.getPublicProfileByWallet(primaryWallet);

            if (profile && wallets.length > 1) {
                // Aggregate points from all wallets for the global view
                let globalTotal = 0;
                for (const w of wallets) {
                    const pSnap = await get(ref(db, `users/${w}/stats/totalPoints`));
                    if (pSnap.exists()) globalTotal += pSnap.val();
                }
                profile.stats.totalPoints = globalTotal;
                profile.stats.level = Math.floor(Math.pow(globalTotal / 100, 2 / 3)) + 1;
            }

            return profile;
        } catch (error) {
            console.error('[Firebase] getPublicProfileByUsername failed:', error);
            return null;
        }
    },

    async getPublicProfileByWallet(walletAddress: string): Promise<UserProfile | null> {
        if (!db) return null;
        try {
            const userSnap = await get(ref(db, `users/${walletAddress}`));
            if (!userSnap.exists()) return null;
            const userData = userSnap.val();

            // Check if this wallet is part of a multi-wallet setup
            const mappingSnap = await get(ref(db, `uid_mapping/${walletAddress}`));
            if (mappingSnap.exists()) {
                const uid = mappingSnap.val();
                const allWalletsSnap = await get(query(ref(db, 'uid_mapping'), orderByValue(), equalTo(uid)));
                if (allWalletsSnap.exists()) {
                    const wallets = Object.keys(allWalletsSnap.val());
                    if (wallets.length > 1) {
                        let globalTotal = 0;
                        for (const w of wallets) {
                            const pSnap = await get(ref(db, `users/${w}/stats/totalPoints`));
                            if (pSnap.exists()) globalTotal += pSnap.val();
                        }
                        userData.stats.totalPoints = globalTotal;
                        userData.stats.level = Math.floor(Math.pow(globalTotal / 100, 2 / 3)) + 1;
                    }
                }
            }

            return {
                ...userData,
                userId: walletAddress,
                walletAddress: walletAddress
            };
        } catch (error) {
            console.error('[Firebase] getPublicProfileByWallet failed:', error);
            return null;
        }
    },

    /**
     * Get actual leaderboard rank for a user.
     * Fetches top 100 to determine rank.
     */
    async getUserRank(walletAddress: string, category: 'xp' | 'wins' | 'adventure' = 'xp'): Promise<number> {
        if (!db) return 0;
        const orderKey = category === 'wins' ? 'multiplayerWins' : category === 'adventure' ? 'adventureBestLevel' : 'totalPoints';
        const lbRef = query(ref(db, 'leaderboard_stats'), orderByChild(orderKey), limitToLast(100));
        const snapshot = await get(lbRef);

        if (snapshot.exists()) {
            const players: string[] = [];
            snapshot.forEach((child) => {
                players.push(child.key as string);
            });
            // Firebase returns ascending, so reverse to get descending rank
            const sortedPlayers = players.reverse();
            const rank = sortedPlayers.indexOf(walletAddress) + 1;
            return rank > 0 ? rank : 0;
        }
        return 0;
    },


    /**
     * Submit a new collection for review.
     * Stores as 'pending' — admin must approve before it becomes 'active'.
     */
    async submitCollection(payload: {
        name: string;
        description: string;
        avatarUrl: string;
        creatorAddresses: string[];
        unitNames: string[];
        submittedBy: string;
        paymentTxId: string;
        verifiedWallets: string[];
    }): Promise<{ success: boolean; id?: string; error?: string }> {
        if (!db) return { success: false, error: 'DB_NOT_READY' };
        try {
            const id = payload.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now();
            const collectionRef = ref(db, `supported_collections/${id}`);
            await set(collectionRef, {
                id,
                name:             payload.name,
                description:      payload.description,
                avatarUrl:        payload.avatarUrl,
                creatorAddresses: payload.creatorAddresses,
                unitNames:        payload.unitNames,
                asaIds:           [],
                status:           'pending',
                submittedBy:      payload.submittedBy.toUpperCase(),
                paymentTxId:      payload.paymentTxId,
                verifiedWallets:  payload.verifiedWallets.map(w => w.toUpperCase()),
                rejectionReason:  '',
                updatedAt:        Date.now(),
                createdAt:        Date.now(),
            });
            return { success: true, id };
        } catch (error: any) {
            console.error('[Firebase] submitCollection failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Admin: Approve a pending collection (sets status → active, runs ASA discovery).
     */
    async approveCollection(id: string, discoveredAsaIds: number[]): Promise<OperationResult> {
        if (!db) return { success: false, error: ErrorCode.DB_NOT_READY };
        try {
            const collectionRef = ref(db, `supported_collections/${id}`);
            await update(collectionRef, {
                status:    'active',
                asaIds:    [...new Set(discoveredAsaIds)],
                updatedAt: Date.now(),
            });
            return { success: true };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    },

    /**
     * Admin: Reject a pending collection with an optional reason.
     */
    async rejectCollection(id: string, reason?: string): Promise<OperationResult> {
        if (!db) return { success: false, error: ErrorCode.DB_NOT_READY };
        try {
            const collectionRef = ref(db, `supported_collections/${id}`);
            await update(collectionRef, {
                status:          'rejected',
                rejectionReason: reason || '',
                updatedAt:       Date.now(),
            });
            return { success: true };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    },

    /**
     * Admin: Edit/patch an existing collection (name, description, avatar, etc).
     */
    async patchCollection(id: string, patch: Partial<SupportedCollection>): Promise<OperationResult> {
        if (!db) return { success: false, error: ErrorCode.DB_NOT_READY };
        try {
            const collectionRef = ref(db, `supported_collections/${id}`);
            await update(collectionRef, { ...patch, updatedAt: Date.now() });
            return { success: true };
        } catch (error: any) {
            return { success: false, message: error.message };
        }
    },

    /**
     * Fetch collections filtered by status.
     */
    async getCollectionsByStatus(status: 'pending' | 'active' | 'rejected'): Promise<SupportedCollection[]> {
        if (!db) return [];

        // Firebase serialises JS arrays as {0:x, 1:y} objects.
        // Normalise every array field back to a real array on read.
        const normalizeCollection = (col: any): SupportedCollection => ({
            ...col,
            // Firebase serialises JS arrays as {0:x,1:y} objects. Re-hydrate each field.
            // asaIds MUST be number[] — use .map(Number) because Object.values() returns strings.
            asaIds:           Array.isArray(col.asaIds)           ? col.asaIds.map(Number)           : Object.values(col.asaIds           ?? {}).map(Number),
            creatorAddresses: Array.isArray(col.creatorAddresses) ? col.creatorAddresses             : Object.values(col.creatorAddresses ?? {}),
            unitNames:        Array.isArray(col.unitNames)        ? col.unitNames                    : Object.values(col.unitNames        ?? {}),
            verifiedWallets:  Array.isArray(col.verifiedWallets)  ? col.verifiedWallets              : Object.values(col.verifiedWallets  ?? {}),
        });

        try {
            const snap = await get(ref(db, 'supported_collections'));
            if (!snap.exists()) return [];
            const all: SupportedCollection[] = [];
            snap.forEach(child => {
                const col = normalizeCollection(child.val());
                if (col.status === status) all.push(col);
                // Legacy entries (no status) count as active
                if (status === 'active' && !col.status) all.push(col);
            });
            // Deduplicate
            const seen = new Set<string>();
            return all.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
        } catch (error) {
            console.error('[Firebase] getCollectionsByStatus failed:', error);
            return [];
        }
    },

    /**
     * Legacy helper: Get all active supported collections.
     */
    async getSupportedCollections(): Promise<SupportedCollection[]> {
        return this.getCollectionsByStatus('active');
    },

    /**
     * Update a collection with new ASA IDs, merging with existing ones.
     */
    async addAsaIdsToCollection(collectionId: string, newIds: number[]): Promise<void> {
        if (!db) return;
        const colRef = ref(db, `supported_collections/${collectionId}`);
        const snap   = await get(colRef);
        
        if (snap.exists()) {
            const data = snap.val();
            // Firebase serialises arrays as {0: x, 1: y} objects — normalize before spread
            const rawExisting = data.asaIds ?? [];
            const existing: number[] = Array.isArray(rawExisting)
                ? rawExisting.map(Number)
                : Object.values(rawExisting).map(Number);
            const merged = Array.from(new Set([...existing, ...newIds]));
            await update(colRef, { asaIds: merged, updatedAt: Date.now() });
            // eslint-disable-next-line no-console
            console.log(`[Firebase] Updated collection ${collectionId} with ${newIds.length} assets (total: ${merged.length})`);
        }
    },

    /**
     * Upload a collection avatar image (Base64 placeholder).
     */
    async uploadCollectionAvatar(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    },

    /**
     * Admin: Delete a supported collection.
     */
    async deleteSupportedCollection(id: string): Promise<OperationResult> {
        if (!db) return { success: false, error: ErrorCode.DB_NOT_READY };
        try {
            const collectionRef = ref(db, `supported_collections/${id}`);
            await set(collectionRef, null);
            return { success: true };
        } catch (error) {
            console.error('Failed to delete supported collection:', error);
            return { success: false, error: ErrorCode.NETWORK_ERROR };
        }
    },

    /**
     * Diagnostic tool to check backend mapping and state.
     */
    async debugGetUserState(): Promise<any> {
        if (!functions) return { error: 'Functions not initialized' };
        const diag = httpsCallable(functions, 'debugGetUserState');
        try {
            const result = await diag();
            return result.data;
        } catch (error: any) {
            console.error('[Firebase] Diagnostic failed:', error);
            throw error;
        }
    },

    /**
     * Diagnostic tool to verify Admin SDK connectivity.
     */
    async checkDatabaseHealth(): Promise<any> {
        if (!functions) return { error: 'Functions not initialized' };
        const health = httpsCallable(functions, 'checkDatabaseHealth');
        try {
            const result = await health();
            return result.data;
        } catch (error: any) {
            console.error('[Firebase] Health check failed:', error);
            throw error;
        }
    },

    /**
     * ping
     */
    async ping(): Promise<any> {
        if (!functions) return { error: 'Functions not initialized' };
        const pingFn = httpsCallable(functions, 'ping');
        try {
            const result = await pingFn();
            return result.data;
        } catch (error: any) {
            console.error('[Firebase] Ping failed:', error);
            throw error;
        }
    },

    /**
     * helloAuthCheck
     */
    async helloAuthCheck(): Promise<any> {
        if (!functions) return { error: 'Functions not initialized' };
        const helloFn = httpsCallable(functions, 'helloAuth');
        try {
            const result = await helloFn();
            return result.data;
        } catch (error: any) {
            console.error('[Firebase] HelloAuth failed:', error);
            throw error;
        }
    },

    /**
     * Saves the minted asset ID to the user's profile.
     */
    async updateUserAvatar(walletAddress: string, assetId: number): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();

        await update(ref(db, `users/${upperId}`), {
            profileImageAssetId: assetId,
            lastActiveAt: serverTimestamp(),
        });

        // Sync to leaderboard
        const snap = await get(ref(db, `users/${upperId}`));
        if (snap.exists()) {
            // Internal call to sync
            const syncFn = (this as any).syncLeaderboardStats;
            if (syncFn) {
                await syncFn(upperId, snap.val());
            }
        }
    },

    /**
     * Increment the boss-kill counter and award tokens atomically.
     * Mini-bosses (5, 15, 25, 35, 45) award 1 token.
     * Major bosses (10, 20, 30, 40, 50) award 2 tokens.
     */
    async recordBossKill(walletAddress: string, tokensToAward: number = 1): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const userRef = ref(db, `users/${upperId}`);

        await runTransaction(userRef, (userData) => {
            if (!userData) return userData;
            if (!userData.adventureProgress) {
                userData.adventureProgress = { brineTokens: 0, unlockedSkills: [], maxLevelReached: 1, bossKills: 0 };
            }

            // Increment bossKills (for stats tracking - now stores token value sum)
            userData.adventureProgress.bossKills = (userData.adventureProgress.bossKills || 0) + tokensToAward;
            
            // Increment brineTokens directly
            userData.adventureProgress.brineTokens = (userData.adventureProgress.brineTokens || 0) + tokensToAward;

            return userData;
        });
        console.log(`[Firebase] Boss kill recorded for ${walletAddress}. Tokens: ${tokensToAward}`);
    },

    /**
     * Persist a claimed barrel drop to the user's inventory.
     * @deprecated Use claimBarrelDropV2 for new inventory model.
     */
    async claimBarrelDrop_LEGACY(walletAddress: string, drop: any): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const inventoryRef = ref(db, `users/${upperId}/adventureProgress/barrelInventory`);
        
        // Use push to add a new unique entry in the inventory
        const newEntryRef = push(inventoryRef);
        await set(newEntryRef, {
            ...drop,
            claimedAt: serverTimestamp()
        });
        console.log(`[Firebase] Barrel drop claimed (pushed) for ${walletAddress}`);
    },

    async openBarrelInventory(walletAddress: string, barrelKey: string, drop: any): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const barrelRef = ref(db, `users/${upperId}/adventureProgress/barrelInventory/${barrelKey}`);
        
        // Update the existing record to mark it as opened and store the revealed drop
        await update(barrelRef, {
            opened: true,
            drop: drop,
            openedAt: serverTimestamp()
        });
        console.log(`[Firebase] Barrel ${barrelKey} opened for ${walletAddress}`);
    },

    /**
     * Reset adventure-specific progress for a user (v1.6.15 migration).
     * Preserves barrelInventory and global points.
     */
    async resetAdventureProgress(walletAddress: string, version: string): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        
        // 1. Reset adventureProgress node (except inventory)
        const progressRef = ref(db, `users/${upperId}/adventureProgress`);
        
        await runTransaction(progressRef, (current) => {
            const inventory = current?.barrelInventory || {};
            return {
                brineTokens: 0,
                unlockedSkills: [],
                maxLevelReached: 0,
                bossKills: 0,
                barrelInventory: inventory,
                version: version
            };
        });

        // 2. Reset bestLevel in stats
        const bestLevelRef = ref(db, `users/${upperId}/stats/adventure/bestLevel`);
        await set(bestLevelRef, 0);
        
        console.log(`[Firebase] Adventure progress reset to ${version} for ${walletAddress}`);
    },

    /**
     * Recalculate and sync Brine Tokens based on adventure milestones vs spent skills.
     * Tokens = (maxLevelReached + bossKills) - cost of unlockedSkills.
     * Note: bossKills now stores the total token value awarded from bosses.
     */
    async syncBrineTokens(walletAddress: string): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const userRef = ref(db, `users/${upperId}`);

        await runTransaction(userRef, (userData) => {
            if (!userData) return userData;

            const adventureProgress = userData.adventureProgress || {};
            const stats = userData.stats || {};
            
            // source milestones - check both potential locations for "bestLevel"
            const bestLevel = Math.max(
                stats.adventure?.bestLevel || 0,
                adventureProgress.maxLevelReached || 0
            );
            const bossKills = adventureProgress.bossKills || 0;
            const unlockedSkills = adventureProgress.unlockedSkills || [];
            
            // Recalculate awarded: 1 per level + actual value stored in bossKills
            const pointsAwarded = bestLevel + bossKills;
            
            // Recalculate spent using SKILL_TREE
            const allSkills = Object.values(SKILL_TREE)
                .flatMap(path => Object.values(path.roads).flatMap(road => road.skills));
            const pointsSpent = allSkills
                .filter(s => unlockedSkills.includes(s.id))
                .reduce((sum, s) => sum + s.cost, 0);
                
            const available = Math.max(0, pointsAwarded - pointsSpent);
            
            userData.adventureProgress = {
                ...adventureProgress,
                brineTokens: available
            };
            
            return userData;
        });
        console.log(`[Firebase] Brine tokens synced for ${walletAddress}`);
    },

    /**
     * Perform a partial update on the adventureProgress node.
     */
    async patchAdventureProgress(walletAddress: string, patch: any): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const progressRef = ref(db, `users/${upperId}/adventureProgress`);
        await update(progressRef, patch);
        console.log(`[Firebase] Adventure progress patched for ${walletAddress}`, patch);
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // BRINE CREDITS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Recompute Brine Credit balance from source of truth.
     * brineCredits = totalPoints - creditsSpent
     *
     * Safe to call on every profile load. Idempotent.
     * Mirrors the pattern of syncBrineTokens for adventure tokens.
     */
    async syncBrineCredits(walletAddress: string): Promise<number> {
        if (!db) return 0;
        const upperId = walletAddress.toUpperCase();
        const userRef = ref(db, `users/${upperId}`);

        let computedBalance = 0;

        await runTransaction(userRef, (userData) => {
            if (!userData) return userData;

            const totalPoints      = userData.stats?.totalPoints   || 0;
            const creditsSpent     = userData.creditsSpent         || 0;
            const purchasedCredits = userData.purchasedCredits     || 0;

            computedBalance = Math.max(0, (totalPoints - creditsSpent) + purchasedCredits);

            userData.brineCredits = computedBalance;
            return userData;
        });

        return computedBalance;
    },

    /**
     * Spend Brine Credits.
     * Increments creditsSpent (the only write needed — balance is derived).
     * Runs a transaction to prevent race conditions.
     * Returns { success, remaining } — success=false if insufficient balance.
     */
    async spendBrineCredits(
        walletAddress: string,
        amount: number,
        reason: string,
    ): Promise<{ success: boolean; remaining: number }> {
        if (!db) return { success: false, remaining: 0 };
        const upperId = walletAddress.toUpperCase();
        const userRef = ref(db, `users/${upperId}`);

        let result = { success: false, remaining: 0 };

        await runTransaction(userRef, (userData) => {
            if (!userData) return userData;

            const totalPoints      = userData.stats?.totalPoints   || 0;
            const creditsSpent     = userData.creditsSpent         || 0;
            const purchasedCredits = userData.purchasedCredits     || 0;
            const balance          = Math.max(0, (totalPoints - creditsSpent) + purchasedCredits);

            if (balance < amount) {
                result = { success: false, remaining: balance };
                return userData; // abort — no change
            }

            // We increment creditsSpent. This remains the "debt" against earned points.
            // If the user has purchased credits, we essentially "pay off" the debt
            // or reduce the surplus.
            userData.creditsSpent = creditsSpent + amount;
            userData.brineCredits  = balance - amount;
            result = { success: true, remaining: balance - amount };
            return userData;
        });

        if (result.success) {
            // Non-blocking audit log
            const logRef = push(ref(db, `users/${upperId}/creditLog`));
            set(logRef, { action: 'spend', amount, reason, at: serverTimestamp() });
        }

        return result;
    },

    /**
     * Get the current computed Brine Credit balance.
     * Reads brineCredits node (kept in sync by syncBrineCredits).
     * Falls back to live computation if node is missing.
     */
    async getBrineCredits(walletAddress: string): Promise<number> {
        if (!db) return 0;
        const upperId = walletAddress.toUpperCase();

        // Try cached node first (fast path)
        const snap = await get(ref(db, `users/${upperId}/brineCredits`));
        if (snap.exists() && typeof snap.val() === 'number' && snap.val() > 0) {
            return snap.val() as number;
        }

        // Cache miss or zero — recompute from source of truth
        return this.syncBrineCredits(walletAddress);
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // INVENTORY — STONES
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Add a stone to the user's stone inventory.
     * Called after barrel open or stone merge.
     */
    async addStoneToInventory(walletAddress: string, stone: Stone): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        await set(
            ref(db, `users/${upperId}/inventory/stones/${stone.id}`),
            { ...stone, addedAt: serverTimestamp() },
        );
    },

    /**
     * Remove a stone from inventory (when socketing into a baguette or merging).
     */
    async removeStoneFromInventory(walletAddress: string, stoneId: string): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        await set(ref(db, `users/${upperId}/inventory/stones/${stoneId}`), null);
    },

    /**
     * Fetch all stones in a user's inventory.
     */
    async getStoneInventory(walletAddress: string): Promise<Stone[]> {
        if (!db) return [];
        const upperId = walletAddress.toUpperCase();
        
        // Use a Map to ensure absolute uniqueness by stone.id
        const stoneMap = new Map<string, Stone>();

        // 1. Fetch from NEW inventory path (Primary)
        const newSnap = await get(ref(db, `users/${upperId}/inventory/stones`));
        if (newSnap.exists()) {
            const raw = newSnap.val();
            const stones = Object.values(raw) as any[];
            stones.forEach(s => {
                if (s && s.id && s.type && STONE_META[s.type as StoneType]) {
                    // Repair iconKey if missing
                    if (!s.iconKey) s.iconKey = STONE_META[s.type as StoneType].iconKey;
                    stoneMap.set(s.id, s as Stone);
                }
            });
        }
        
        // 2. Fetch from LEGACY barrelInventory path (Fallback)
        const legacySnap = await get(ref(db, `users/${upperId}/adventureProgress/barrelInventory`));
        if (legacySnap.exists()) {
            const items = Object.values(legacySnap.val()) as any[];
            items.forEach(item => {
                // Legacy stones could be:
                // 1. { type: 'stone', stone: { ... } }
                // 2. { type: 'stone', ...stoneFields }
                // 3. { opened: true, drop: { type: 'stone', stone: { ... } } }
                const s = item.stone || item.drop?.stone || (item.type === 'stone' && item.id ? item : null);
                
                if (s && s.id && (item.type === 'stone' || item.drop?.type === 'stone')) {
                    // Only add if not already in the map (New path takes precedence)
                    if (!stoneMap.has(s.id)) {
                        const type = s.type as StoneType;
                        if (STONE_META[type]) {
                            // Repair missing fields for legacy records
                            if (!s.iconKey) s.iconKey = STONE_META[type].iconKey;
                            if (!s.label) s.label = `${STONE_META[type].label} +${s.value || 0}%`;
                            if (!s.color) s.color = STONE_META[type].color;
                            stoneMap.set(s.id, s as Stone);
                        }
                    }
                }
            });
        }
        
        return Array.from(stoneMap.values());
    },

    /**
     * Update the stones socketed into a baguette.
     * Atomically manages inventory: moves stones between vault and baguette.
     */
    async updateEquippedStones(
        walletAddress: string,
        baguetteId: string,
        newStones: (Stone | null)[],
    ): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const baguetteRef = ref(db, `users/${upperId}/inventory/baguettes/${baguetteId}`);

        // Fetch current state to determine delta
        const bSnap = await get(baguetteRef);
        if (!bSnap.exists()) throw new Error("Baguette not found");
        const currentBaguette = bSnap.val() as Baguette;
        const oldStones = currentBaguette.stones || [];

        const oldIds = oldStones.filter(Boolean).map(s => (s as Stone).id);
        const newIds = newStones.filter(Boolean).map(s => (s as Stone).id);

        // Stones to return to vault (in old but not in new)
        const toReturn = oldStones.filter(s => s && !newIds.includes(s.id)) as Stone[];
        // Stones to remove from vault (in new but not in old)
        const toTake = newStones.filter(s => s && !oldIds.includes(s.id)) as Stone[];

        const patch: Record<string, any> = {};

        // 1. Return unsocketed stones to vault
        toReturn.forEach(s => {
            patch[`users/${upperId}/inventory/stones/${s.id}`] = { ...s, addedAt: serverTimestamp() };
        });

        // 2. Take newly socketed stones from vault
        toTake.forEach(s => {
            patch[`users/${upperId}/inventory/stones/${s.id}`] = null;
        });

        // 3. Update baguette socket array
        patch[`users/${upperId}/inventory/baguettes/${baguetteId}/stones`] = newStones;

        await update(ref(db), patch);
        console.log(`[Firebase] Baguette ${baguetteId} sockets updated.`);
    },

    /**
     * Merge 5 stones of the same rarity into 1 random stone of the next rarity.
     * Level 15 gate enforced here — call only after confirming playerLevel >= 15.
     * Returns the new stone, or null if merge is invalid.
     */
    async mergeStoneTransaction(
        walletAddress: string,
        stoneIds: string[],   // must be exactly 5 ids of the same rarity
        inputRarity: Rarity,
    ): Promise<Stone | null> {
        if (!db || stoneIds.length !== 5) return null;
        const upperId = walletAddress.toUpperCase();

        // Verify all 5 stones exist and match rarity before mutating
        const stonesRef = ref(db, `users/${upperId}/inventory/stones`);
        const snap = await get(stonesRef);
        if (!snap.exists()) return null;
        const inventory = snap.val() as Record<string, Stone>;

        for (const id of stoneIds) {
            if (!inventory[id] || inventory[id].rarity !== inputRarity) return null;
        }

        const newStone = mergeStones(inputRarity);
        if (!newStone) return null;

        // Atomic: remove 5, add 1
        const patch: Record<string, any> = {};
        for (const id of stoneIds) patch[id] = null;
        patch[newStone.id] = { ...newStone, addedAt: serverTimestamp() };

        await update(stonesRef, patch);
        return newStone;
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // INVENTORY — INGREDIENTS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Get current ingredient counts for a user.
     * Returns zeroes for any missing keys (Phase 2: tradeable flag ready).
     */
    async getIngredients(walletAddress: string): Promise<Record<IngredientType, number>> {
        if (!db) return { pit_dust: 0, brine_drops: 0, active_culture: 0, salt_stone: 0 };
        const upperId = walletAddress.toUpperCase();
        const snap = await get(ref(db, `users/${upperId}/inventory/ingredients`));
        const defaults = { pit_dust: 0, brine_drops: 0, active_culture: 0, salt_stone: 0 };
        if (!snap.exists()) return defaults;
        return { ...defaults, ...snap.val() } as Record<IngredientType, number>;
    },

    /**
     * Increment a single ingredient by qty (default 1).
     * Used when a barrel drops an ingredient.
     */
    async addIngredient(
        walletAddress: string,
        ingredient: IngredientType,
        qty: number = 1,
    ): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        const ingredRef = ref(db, `users/${upperId}/inventory/ingredients/${ingredient}`);
        await runTransaction(ingredRef, (current: number | null) => (current ?? 0) + qty);
    },

    /**
     * Craft a Starter Dough from 4 ingredients.
     * Atomically deducts ingredients and adds the dough to inventory.
     * Returns the new StarterDough or null if insufficient ingredients.
     */
    async craftStarterDough(walletAddress: string): Promise<StarterDough | null> {
        if (!db) return null;
        const upperId = walletAddress.toUpperCase();
        const ingredientsRef = ref(db, `users/${upperId}/inventory/ingredients`);
        let crafted: StarterDough | null = null;

        await runTransaction(ingredientsRef, (current: Record<IngredientType, number> | null) => {
            const counts = {
                pit_dust:       current?.pit_dust       ?? 0,
                brine_drops:    current?.brine_drops    ?? 0,
                active_culture: current?.active_culture ?? 0,
                salt_stone:     current?.salt_stone     ?? 0,
            };

            // Verify recipe requirements
            for (const [ing, qty] of Object.entries(CRAFT_RECIPE) as [IngredientType, number][]) {
                if (counts[ing] < qty) return current; // abort
            }

            // Deduct ingredients
            for (const [ing, qty] of Object.entries(CRAFT_RECIPE) as [IngredientType, number][]) {
                counts[ing] -= qty;
            }

            crafted = rollStarterDoughGacha();
            crafted.source = 'crafted';
            return counts;
        });

        if (!crafted) return null;

        // Persist the new dough
        await set(
            ref(db, `users/${upperId}/inventory/starterDoughs/${(crafted as StarterDough).id}`),
            { ...(crafted as StarterDough), addedAt: serverTimestamp() },
        );

        return crafted as StarterDough;
    },

    /**
     * Deconstruct (recycle) a baguette into its 4 base ingredients.
     * Removes the baguette from inventory, increments all 4 ingredient counts by 1.
     * Cannot deconstruct a listed (marketplace) baguette.
     */
    async deconstructBaguette(walletAddress: string, baguetteId: string): Promise<boolean> {
        if (!db) return false;
        const upperId = walletAddress.toUpperCase();
        const baguetteRef = ref(db, `users/${upperId}/inventory/baguettes/${baguetteId}`);
        const snap = await get(baguetteRef);

        if (!snap.exists()) return false;
        const baguette = snap.val() as Baguette;
        if (baguette.isListed) return false; // can't recycle a listed baguette

        // Un-socket all stones back to inventory before deleting
        const stonePatch: Record<string, any> = {};
        for (const stone of baguette.stones) {
            if (stone) stonePatch[stone.id] = { ...stone, addedAt: serverTimestamp() };
        }
        if (Object.keys(stonePatch).length > 0) {
            await update(ref(db, `users/${upperId}/inventory/stones`), stonePatch);
        }

        // Award 1 of each ingredient
        const ingredPatch: Record<string, any> = {};
        const ingredientsSnap = await get(ref(db, `users/${upperId}/inventory/ingredients`));
        const current = ingredientsSnap.exists()
            ? ingredientsSnap.val()
            : { pit_dust: 0, brine_drops: 0, active_culture: 0, salt_stone: 0 };

        ingredPatch['pit_dust']       = (current.pit_dust       ?? 0) + 1;
        ingredPatch['brine_drops']    = (current.brine_drops    ?? 0) + 1;
        ingredPatch['active_culture'] = (current.active_culture ?? 0) + 1;
        ingredPatch['salt_stone']     = (current.salt_stone     ?? 0) + 1;

        await update(ref(db, `users/${upperId}/inventory/ingredients`), ingredPatch);

        // Delete the baguette
        await set(baguetteRef, null);
        return true;
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // INVENTORY — STARTER DOUGHS
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Get all starter doughs in a user's inventory.
     */
    async getStarterDoughs(walletAddress: string): Promise<StarterDough[]> {
        if (!db) return [];
        const upperId = walletAddress.toUpperCase();
        const snap = await get(ref(db, `users/${upperId}/inventory/starterDoughs`));
        if (!snap.exists()) return [];
        return Object.values(snap.val()) as StarterDough[];
    },

    /**
     * Add a starter dough to inventory.
     * Used by: Level 5 gift, barrel rare drop, gacha purchase.
     */
    async addStarterDough(walletAddress: string, dough: StarterDough): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        await set(
            ref(db, `users/${upperId}/inventory/starterDoughs/${dough.id}`),
            { ...dough, addedAt: serverTimestamp() },
        );
    },

    /**
     * Award Level 5 starter dough gift if not already received.
     * Safe to call on every level-up — idempotent via giftClaimed flag.
     */
    async claimLevel5StarterGift(walletAddress: string): Promise<StarterDough | null> {
        if (!db) return null;

        // Internal Guard: Only verified sessions can write to the user node
        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.isAnonymous) {
            console.warn('[Firebase] claimLevel5StarterGift skipped — no verified auth session');
            return null;
        }

        const upperId = walletAddress.toUpperCase();
        const giftClaimedRef = ref(db, `users/${upperId}/brewery/level5GiftClaimed`);
        const snap = await get(giftClaimedRef);

        if (snap.exists() && snap.val() === true) return null; // already claimed

        const dough: StarterDough = {
            id: `dough_gift_${Date.now()}`,
            strainId: 'pit_culture',
            rarity: 'common',
            source: 'gift',
            acquiredAt: Date.now(),
            tradeable: false,
        };

        await set(giftClaimedRef, true);
        await set(
            ref(db, `users/${upperId}/inventory/starterDoughs/${dough.id}`),
            { ...dough, addedAt: serverTimestamp() },
        );

        return dough;
    },

    /**
     * Purchase a starter dough via gacha — USDC path (1 USDC per pull).
     * Payment validation happens on-chain before calling this.
     * Phase 2: add Algorand transaction ID parameter for audit trail.
     */
    async purchaseStarterDoughGacha(walletAddress: string): Promise<StarterDough> {
        const dough = rollStarterDoughGacha();
        await firebaseUtils.addStarterDough(walletAddress, dough);
        return dough;
    },

    /**
     * Purchase a starter dough using Brine Credits.
     * Cost is looked up from STARTER_CREDIT_COST in PickleBarrel.ts.
     */
    async purchaseStarterDoughWithCredits(
        walletAddress: string,
        strainId: StarterStrainId,
        creditCost: number,
    ): Promise<{ success: boolean; dough?: StarterDough; message?: string }> {
        const spend = await firebaseUtils.spendBrineCredits(walletAddress, creditCost, `starter_dough_${strainId}`);
        if (!spend.success) {
            return { success: false, message: 'Insufficient Brine Credits.' };
        }
        const dough: StarterDough = {
            id: `dough_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
            strainId,
            rarity: STARTER_STRAINS[strainId].rarity,
            source: 'gacha',
            acquiredAt: Date.now(),
            tradeable: false,
        };
        await firebaseUtils.addStarterDough(walletAddress, dough);
        return { success: true, dough };
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // INVENTORY — BAGUETTES
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Get all baguettes in a user's inventory.
     */
    async getBaguetteInventory(walletAddress: string): Promise<Baguette[]> {
        if (!db) return [];
        const upperId = walletAddress.toUpperCase();
        const snap = await get(ref(db, `users/${upperId}/inventory/baguettes`));
        if (!snap.exists()) return [];
        return Object.values(snap.val()) as Baguette[];
    },

    /**
     * Save a baguette to inventory. Called on bake completion.
     */
    async addBaguetteToInventory(walletAddress: string, baguette: Baguette): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        await set(
            ref(db, `users/${upperId}/inventory/baguettes/${baguette.id}`),
            { ...baguette, addedAt: serverTimestamp() },
        );
    },

    /**
     * Socket a stone into a baguette.
     * Atomically moves stone from inventory → baguette socket.
     * Returns updated Baguette or null if socket is unavailable or rarity mismatch.
     */
    async socketStoneIntoBaguette(
        walletAddress: string,
        baguetteId: string,
        stoneId: string,
    ): Promise<Baguette | null> {
        if (!db) return null;
        const upperId = walletAddress.toUpperCase();

        const [baguetteSnap, stoneSnap] = await Promise.all([
            get(ref(db, `users/${upperId}/inventory/baguettes/${baguetteId}`)),
            get(ref(db, `users/${upperId}/inventory/stones/${stoneId}`)),
        ]);

        if (!baguetteSnap.exists() || !stoneSnap.exists()) return null;

        const baguette = baguetteSnap.val() as Baguette;
        const stone = stoneSnap.val() as Stone;

        // Import socketStone from PickleBarrel for compatibility check
        const { socketStone, canSocket } = await import('../data/PickleBarrel');
        if (!canSocket(stone, baguette)) return null;

        const updated = socketStone(baguette, stone);
        if (!updated) return null;

        await Promise.all([
            set(ref(db, `users/${upperId}/inventory/baguettes/${baguetteId}`), updated),
            set(ref(db, `users/${upperId}/inventory/stones/${stoneId}`), null),
        ]);

        return updated;
    },

    /**
     * Remove a stone from a baguette socket back to inventory.
     * Returns the stone, or null if socket was empty.
     */
    async unsocketStoneFromBaguette(
        walletAddress: string,
        baguetteId: string,
        socketIndex: number,
    ): Promise<Stone | null> {
        if (!db) return null;
        const upperId = walletAddress.toUpperCase();
        const baguetteRef = ref(db, `users/${upperId}/inventory/baguettes/${baguetteId}`);
        const snap = await get(baguetteRef);
        if (!snap.exists()) return null;

        const baguette = snap.val() as Baguette;
        const { unsocketStone } = await import('../data/PickleBarrel');
        const result = unsocketStone(baguette, socketIndex);
        if (!result) return null;

        await Promise.all([
            set(baguetteRef, result.baguette),
            set(
                ref(db, `users/${upperId}/inventory/stones/${result.stone.id}`),
                { ...result.stone, addedAt: serverTimestamp() },
            ),
        ]);

        return result.stone;
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // BREWERY — OVEN MANAGEMENT
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Get oven state for both slots.
     * Returns null for each slot if empty or not unlocked.
     */
    async getOvenSlots(walletAddress: string): Promise<{
        primary: ActiveBake | null;
        secondary: ActiveBake | null;
        secondOvenUnlocked: boolean;
    }> {
        if (!db) return { primary: null, secondary: null, secondOvenUnlocked: false };
        const upperId = walletAddress.toUpperCase();
        const snap = await get(ref(db, `users/${upperId}/brewery`));
        if (!snap.exists()) return { primary: null, secondary: null, secondOvenUnlocked: false };
        const data = snap.val();
        return {
            primary:             data?.ovens?.primary   ?? null,
            secondary:           data?.ovens?.secondary ?? null,
            secondOvenUnlocked:  data?.secondOvenUnlocked ?? false,
        };
    },

    /**
     * Unlock the second oven slot (permanent, 20 USDC — payment validated on-chain before calling).
     * Level 10 gate should be enforced in UI before calling.
     * Phase 2: mint Second Oven NFT here.
     */
    async unlockSecondOven(walletAddress: string): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();
        await update(ref(db, `users/${upperId}/brewery`), {
            secondOvenUnlocked:    true,
            secondOvenUnlockedAt:  serverTimestamp(),
        });
    },

    /**
     * Start a bake in a given oven slot.
     * Removes the starter dough from inventory, initializes oven slot state.
     * Proofing window opens immediately (2h window).
     * Rising window opens at 33% of total bake time (2h window).
     */
    async startBake(
        walletAddress: string,
        doughId: string,
        slot: 'primary' | 'secondary',
    ): Promise<ActiveBake | null> {
        if (!db) return null;
        const upperId = walletAddress.toUpperCase();

        // Verify slot is empty
        const ovenSnap = await get(ref(db, `users/${upperId}/brewery/ovens/${slot}`));
        if (ovenSnap.exists() && ovenSnap.val() !== null) return null;

        // Verify dough exists
        const doughSnap = await get(
            ref(db, `users/${upperId}/inventory/starterDoughs/${doughId}`),
        );
        if (!doughSnap.exists()) return null;
        const dough = doughSnap.val() as StarterDough;
        const strain = STARTER_STRAINS[dough.strainId];

        const now = Date.now();
        const completesAt = now + strain.bakeMs;

        // Proofing window: opens now, closes at 25% of bake time (min 2h)
        const proofingClose  = now + Math.max(2 * 60 * 60 * 1000, strain.bakeMs * 0.25);
        // Rising window: opens at 33% of bake time, closes at 66%
        const risingOpen     = now + strain.bakeMs * 0.33;
        const risingClose    = now + strain.bakeMs * 0.66;

        // Pre-resolve final output (hidden until claim)
        const { resolvedSockets, resolvedRarity } = resolveBake(dough.strainId, false, false);

        const bake: ActiveBake = {
            id:           `bake_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
            doughId,
            strainId:     dough.strainId,
            rarity:       dough.rarity,
            stages: {
                proofing: {
                    windowOpenAt:  now,
                    windowCloseAt: proofingClose,
                    completed:     false,
                    completedAt:   null,
                },
                rising: {
                    windowOpenAt:  risingOpen,
                    windowCloseAt: risingClose,
                    completed:     false,
                    completedAt:   null,
                },
                baking: {
                    windowOpenAt:  risingClose,
                    windowCloseAt: completesAt,
                    completed:     false,
                    completedAt:   null,
                },
            },
            startedAt:        now,
            completesAt,
            currentStage:     'proofing',
            _resolvedSockets: resolvedSockets,
            _resolvedRarity:  resolvedRarity,
        };

        // Consume the dough and write oven state atomically
        await Promise.all([
            set(ref(db, `users/${upperId}/inventory/starterDoughs/${doughId}`), null),
            set(ref(db, `users/${upperId}/brewery/ovens/${slot}`), bake),
        ]);

        return bake;
    },

    /**
     * Record a care action (proofing swaddle or rising feed) on an active bake.
     * Returns updated bake or null if window is closed or stage already completed.
     */
    async recordCareAction(
        walletAddress: string,
        slot: 'primary' | 'secondary',
        stage: 'proofing' | 'rising',
    ): Promise<ActiveBake | null> {
        if (!db) return null;
        const upperId = walletAddress.toUpperCase();
        const ovenRef = ref(db, `users/${upperId}/brewery/ovens/${slot}`);

        let updated: ActiveBake | null = null;

        await runTransaction(ovenRef, (bake: ActiveBake | null) => {
            if (!bake) return bake;
            const stageData = bake.stages[stage];
            if (!stageData) return bake;

            const now = Date.now();
            if (stageData.completed) return bake;             // already done
            if (now > stageData.windowCloseAt) return bake;  // window closed
            if (now < stageData.windowOpenAt) return bake;   // window not open yet

            bake.stages[stage] = {
                ...stageData,
                completed:   true,
                completedAt: now,
            };

            // Advance currentStage label for UI
            if (stage === 'proofing') bake.currentStage = 'rising';
            if (stage === 'rising')   bake.currentStage = 'baking';

            updated = bake;
            return bake;
        });

        return updated;
    },

    /**
     * Claim a completed bake.
     * Evaluates neglect penalties, generates the Baguette, clears the oven slot.
     * Returns the new Baguette or null if bake isn't complete yet.
     */
    async claimBake(
        walletAddress: string,
        slot: 'primary' | 'secondary',
    ): Promise<Baguette | null> {
        if (!db) return null;
        const upperId = walletAddress.toUpperCase();
        const ovenSnap = await get(ref(db, `users/${upperId}/brewery/ovens/${slot}`));

        if (!ovenSnap.exists() || !ovenSnap.val()) return null;
        const bake = ovenSnap.val() as ActiveBake;

        if (Date.now() < bake.completesAt) return null; // not done yet

        const missedProofing = !bake.stages.proofing.completed;
        const missedRising   = !bake.stages.rising.completed;

        const { resolvedSockets, resolvedRarity } = resolveBake(
            bake.strainId,
            missedProofing,
            missedRising,
        );

        const strain = STARTER_STRAINS[bake.strainId];
        const baguette: Baguette = {
            id:        `bag_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
            name:      `${strain.name} Baguette`,
            rarity:    resolvedRarity,
            maxSockets: resolvedSockets,
            stones:    Array(resolvedSockets).fill(null),
            strainId:  bake.strainId,
            bakedAt:   Date.now(),
            asaId:     null,     // Phase 2: mint ARC19 here
            isListed:  false,
        };

        await Promise.all([
            set(ref(db, `users/${upperId}/brewery/ovens/${slot}`), null),
            set(
                ref(db, `users/${upperId}/inventory/baguettes/${baguette.id}`),
                { ...baguette, addedAt: serverTimestamp() },
            ),
        ]);

        return baguette;
    },

    /**
     * Apply a boost to an active bake.
     * Handles credit spending and oven state update in one call.
     * Boost types:
     *   'hot_shot'       — reduce completesAt by 25%
     *   'dill_blessing'  — +1 to resolved socket count (USDC only, max 1 per bake)
     *   'insulation'     — mark proofing as completed even if missed
     *   'extra_feed'     — mark rising as completed even if missed
     */
    async applyOvenBoost(
        walletAddress: string,
        slot: 'primary' | 'secondary',
        boostType: 'hot_shot' | 'dill_blessing' | 'insulation' | 'extra_feed',
        creditCost: number, // 0 if USDC-only boost
    ): Promise<{ success: boolean; message?: string }> {
        if (!db) return { success: false, message: 'Database unavailable.' };
        const upperId = walletAddress.toUpperCase();

        // Spend credits if applicable
        if (creditCost > 0) {
            const spend = await firebaseUtils.spendBrineCredits(
                walletAddress,
                creditCost,
                `boost_${boostType}`,
            );
            if (!spend.success) return { success: false, message: 'Insufficient Brine Credits.' };
        }

        const ovenRef = ref(db, `users/${upperId}/brewery/ovens/${slot}`);

        await runTransaction(ovenRef, (bake: ActiveBake | null) => {
            if (!bake) return bake;
            const now = Date.now();

            switch (boostType) {
                case 'hot_shot': {
                    const remaining = bake.completesAt - now;
                    bake.completesAt = now + Math.floor(remaining * 0.75);
                    break;
                }
                case 'dill_blessing': {
                    if (!bake._dillBlessingUsed) {
                        bake._resolvedSockets = Math.min(
                            bake._resolvedSockets + 1,
                            STARTER_STRAINS[bake.strainId].socketCeiling,
                        );
                        (bake as any)._dillBlessingUsed = true;
                    }
                    break;
                }
                case 'insulation': {
                    if (!bake.stages.proofing.completed) {
                        bake.stages.proofing.completed   = true;
                        bake.stages.proofing.completedAt = now;
                    }
                    break;
                }
                case 'extra_feed': {
                    if (!bake.stages.rising.completed) {
                        bake.stages.rising.completed   = true;
                        bake.stages.rising.completedAt = now;
                    }
                    break;
                }
            }

            return bake;
        });

        return { success: true };
    },

    // ─────────────────────────────────────────────────────────────────────────────
    // BARREL INVENTORY (migrated path)
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Updated claimBarrelDrop — writes to new inventory path.
     * Handles stone, ingredient, and rare starter_dough drops.
     * The old adventureProgress/barrelInventory path is kept for legacy reads
     * until a migration script is run (see note below).
     *
     * NOTE: Drop in replacement for the existing claimBarrelDrop.
     * Rename existing function to claimBarrelDrop_LEGACY before appending this.
     */
    async claimBarrelDropV2(walletAddress: string, drop: any): Promise<void> {
        if (!db) return;
        const upperId = walletAddress.toUpperCase();

        if (drop.type === 'stone' && drop.stone) {
            await firebaseUtils.addStoneToInventory(walletAddress, drop.stone);
        } else if (drop.type === 'ingredient' && drop.ingredient) {
            await firebaseUtils.addIngredient(walletAddress, drop.ingredient);
        } else if (drop.type === 'starter_dough' && drop.starterDough) {
            await firebaseUtils.addStarterDough(walletAddress, drop.starterDough);
        }

        // Write a compact receipt to inventory/barrels for history display
        const receiptsRef = push(ref(db, `users/${upperId}/inventory/barrels`));
        await set(receiptsRef, {
            rarity:    drop.rarity,
            type:      drop.type,
            claimedAt: serverTimestamp(),
        });
    }
};
